import axios from "axios";
import { getApiBase } from "./apiBase";

const API = axios.create({ baseURL: getApiBase() });

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

export async function getExamPassword() {
  try {
    const { data } = await API.get('/api/settings/exam-password');
    return data.password;
  } catch (error) {
    console.error("Error fetching exam password:", error);
    return "123456";
  }
}

export async function updateExamPassword(password) {
  try {
    const { data } = await API.post('/api/settings/exam-password', { password });
    return data.success;
  } catch (error) {
    console.error("Error updating exam password:", error);
    throw error;
  }
}

export async function verifyExamPassword(password) {
  try {
    const { data } = await API.post('/api/settings/verify-password', { password });
    return data.valid;
  } catch (error) {
    console.error("Error verifying exam password:", error);
    return false;
  }
}

export async function updateOperationPassword(password) {
  try {
    const { data } = await API.post('/api/settings/operation-password', { password });
    return data.success;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update operation password.'));
  }
}

export async function verifyOperationPassword(password) {
  try {
    const { data } = await API.post('/api/settings/verify-operation-password', { password });
    return data.valid === true;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      throw new Error('Operation-password API was not found. Restart the server from the updated server folder.');
    }
    throw new Error(getErrorMessage(error, 'Unable to verify the operation password.'));
  }
}

export async function getTheologyPassword() {
  try {
    const { data } = await API.get('/api/settings/theology-password');
    return data.password;
  } catch (error) {
    console.error("Error fetching theology password:", error);
    return "123456";
  }
}

export async function updateTheologyPassword(password) {
  try {
    const { data } = await API.post('/api/settings/theology-password', { password });
    return data.success;
  } catch (error) {
    console.error("Error updating theology password:", error);
    throw error;
  }
}

export async function verifyTheologyPassword(password) {
  try {
    const { data } = await API.post('/api/settings/verify-theology-password', { password });
    return data;
  } catch (error) {
    console.error("Error verifying theology password:", error);
    return { valid: false, requirePassword: true };
  }
}

export async function getTheologyAccess() {
  try {
    const { data } = await API.get('/api/settings/theology-access');
    return data;
  } catch (error) {
    console.error("Error fetching theology access settings:", error);
    return { requirePassword: true };
  }
}

export async function updateTheologyAccess({ password, requirePassword }) {
  try {
    const { data } = await API.post('/api/settings/theology-access', { password, requirePassword });
    return data.success;
  } catch (error) {
    console.error("Error updating theology access settings:", error);
    throw new Error(getErrorMessage(error, "Failed to update theology access settings."));
  }
}



export async function getMathAccess() {
  try {
    const { data } = await API.get('/api/settings/math-access');
    return data;
  } catch (error) {
    console.error("Error fetching math access settings:", error);
    return { requirePassword: true };
  }
}

export async function updateMathAccess({ password, requirePassword }) {
  try {
    const { data } = await API.post('/api/settings/math-access', { password, requirePassword });
    return data.success;
  } catch (error) {
    console.error("Error updating math access settings:", error);
    throw new Error(getErrorMessage(error, "Failed to update math access settings."));
  }
}

export async function verifyMathPassword(password) {
  try {
    const { data } = await API.post('/api/settings/verify-math-password', { password });
    return data;
  } catch (error) {
    console.error("Error verifying math password:", error);
    return { valid: false, requirePassword: true };
  }
}

export async function getVocabDeletePassword() {
  try {
    const { data } = await API.get('/api/settings/vocab-delete-password');
    return data.password;
  } catch {
    return "";
  }
}

export async function updateVocabDeletePassword(password) {
  try {
    const { data } = await API.post('/api/settings/vocab-delete-password', { password });
    return data.success;
  } catch (error) {
    throw new Error(error?.response?.data?.error || "Failed to update vocab delete password.");
  }
}

export async function verifyVocabDeletePassword(password) {
  try {
    const { data } = await API.post('/api/settings/verify-vocab-delete-password', { password });
    return data.valid === true;
  } catch {
    return false;
  }
}

/**
 * Verify password on the server, and if correct, receive an IP-bound session token
 * valid for 5 minutes. Only the requesting IP can use this token for delete operations.
 * Returns { valid: true, token, expiresInMs } on success, or { valid: false, error } on failure.
 */
export async function createVocabDeleteSession(password) {
  try {
    const { data } = await API.post('/api/settings/create-vocab-delete-session', { password });
    return data; // { valid: true, token, expiresInMs } or { valid: false, error }
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401) return { valid: false, error: "Incorrect password" };
    return { valid: false, error: "Unable to create delete session" };
  }
}

/**
 * Verify exam generation password on the server, and if correct, receive an IP-bound session token
 * valid for 5 minutes. Only the requesting IP can use this token for exam generation.
 * Returns { valid: true, token, expiresInMs } on success, or { valid: false, error } on failure.
 */
export async function createExamGenSession(password) {
  try {
    const { data } = await API.post('/api/settings/create-exam-gen-session', { password });
    return data; // { valid: true, token, expiresInMs } or { valid: false, error }
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401) return { valid: false, error: "Incorrect password" };
    return { valid: false, error: "Unable to create exam generation session" };
  }
}

export async function verifyExamGenToken(token) {
  try {
    const { data } = await API.post('/api/settings/verify-exam-gen-token', { token });
    return data.valid === true;
  } catch {
    return false;
  }
}

